import React from 'react';
import fetch from '@packages/utilities/fetch';
import {Set, fromJS} from 'immutable';
import {isEqual} from '@packages/utilities/memo';
import {useQuery, useQueries, UseQueryOptions} from '@tanstack/react-query';
import chunk from 'lodash/chunk';
import {DEFAULT_SKU_SLUG} from '@packages/systems/commerce/constants';
import {unwrap} from '../helpers';
import {getCmsPreviewProjectParams} from '../utils/getCmsPreviewProjectParams';
import {getCMSBranchId} from '../utils/activeCMSBranchId';
import {useKeepPreviousCMSBranchData} from './useKeepPreviousCMSBranchData';
import type {CollectionRecordType as CollectionType} from '@packages/systems/dynamo/records/CollectionRecord';
import type {
  GetCollectionItemsResponse,
  GetCollectionItemsResponseAsJs,
  ReferencedCollectionItems,
  CMSItemsQueryOptions,
} from '@packages/systems/cms/types';
import {isProductsCollection} from '@packages/systems/commerce/utils/CommerceCollectionUtils';
import {receiveRawItems} from '@packages/systems/cms/CollectionStore';
import {ItemRecordType} from '@packages/systems/core/records/ItemRecord';
import type {Locale} from '@packages/systems/localization-types';
import {selectSiteShortNameFromSharedStores} from '@packages/systems/cms/client-selectors';
import {
  selectAllLocales,
  useLocalizationSelector,
} from '@packages/systems/localization';
import {
  Dispatch,
  useDesignerSelector,
  useDispatch,
} from '@packages/systems/designer/flux';

const DEFAULT_PAGE_LIMIT = 100;
export const DEFAULT_SORT = '-created-on';
export const DEFAULT_DEBOUNCED_WAIT = 200;
const STALE_TIME = 5 * 60 * 1000;

const flattenEcommerceItems = (
  items: GetCollectionItemsResponseAsJs['items']
) => {
  return items.reduce<GetCollectionItemsResponseAsJs['items']>((acc, item) => {
    const defaultSkuID = item.product[DEFAULT_SKU_SLUG];
    const defaultSku = item.skus.find(
      (sku: {_id: string}) => sku._id === defaultSkuID
    );
    if (defaultSku) {
      // we're safe to mutate here and we choose to do it to skip a new object creation
      item.product.price = defaultSku.price;
    }
    acc.push(item.product);
    acc.push(...item.skus);
    return acc;
  }, []);
};

export const fetchCollectionItems = (
  collection: CollectionType | null = null,
  siteShortName: string | null = '',
  {
    page = 0,
    limit = DEFAULT_PAGE_LIMIT,
    query = '',
    sort = DEFAULT_SORT,
    fields = '',
    format = undefined,
    locales,
    cmsBranchId = getCMSBranchId(),
  }: CMSItemsQueryOptions & {
    // A query passes the branch its key was built with, so a retry or refetch
    // after a switch still reads the branch it is cached under.
    cmsBranchId?: string | null;
  } = {}
): Promise<GetCollectionItemsResponse> => {
  if (typeof collection === 'string') {
    // this is a safety check for any callsites that are passing in an `any` type
    if (process.env.NODE_ENV === 'development') {
      throw new Error(
        `useCollectionItems() hooks must be provided the collection itself and not the ID`
      );
    }
  }

  if (!collection || !siteShortName) {
    return Promise.reject();
  }

  const collectionId = collection._id || '';

  if (!collectionId) {
    return Promise.reject();
  }

  const offset = page === 0 ? 0 : page * limit;

  const params: {
    [key: string]: any;
  } = {
    target: 'staging',
    offset,
    limit,
    // the backend technically allows sorting by multiple slugs, but the frontend doesn't
    'sort[]': sort,
  };

  if (format) {
    params.format = format;
  }

  if (query) {
    params.text = query;
  }

  const useCommerceProductsEndpoint =
    isProductsCollection(collection) &&
    // The /commerce/products API doesn't support formatting itemRefs
    format !== 'itemRef';

  const {previewId, projectId} = getCmsPreviewProjectParams();

  if (previewId) {
    params.preview = previewId;
  }

  if (projectId) {
    params.project = projectId;
  }

  // Only the CMS items API reads CMS branches. Ecommerce products always read main.
  if (cmsBranchId && !useCommerceProductsEndpoint && !projectId) {
    params.cmsBranchId = cmsBranchId;
  }

  let queryParams = new URLSearchParams(params).toString();

  if (fields) {
    queryParams += `&${fields}`;
  }

  if (Array.isArray(locales) && locales.length > 0) {
    queryParams += locales
      .map((locale) => (locale.cmsId ? `&locales[]=${locale.cmsId}` : ''))
      .join('');
  }

  const endpoint = (() => {
    if (useCommerceProductsEndpoint) {
      return `/api/sites/${siteShortName}/commerce/products?${queryParams}`;
    }
    if (projectId) {
      return `/comment/api/v1/collections/${collectionId}/items?${queryParams}`;
    }

    return `/api/v1/collections/${collectionId}/items?${queryParams}`;
  })();

  return fetch(endpoint)
    .then(unwrap)
    .then((response: GetCollectionItemsResponseAsJs) => {
      const responseItems = response.items || [];

      const items: GetCollectionItemsResponse['items'] =
        useCommerceProductsEndpoint
          ? flattenEcommerceItems(responseItems).map((item) => fromJS(item))
          : responseItems.map((item) => fromJS(item));

      return {
        ...response,
        collectionId: collection._id,
        items,
      };
    });
};

const getCollectionItemsQueryKey = (
  collectionId: string,
  {
    page,
    limit,
    query,
    sort,
    format,
    fields,
    locales,
    cmsBranchId,
  }: CMSItemsQueryOptions & {cmsBranchId: string | null}
) => {
  const {previewId} = getCmsPreviewProjectParams();

  return [
    'collection',
    collectionId,
    'items',
    {
      page,
      limit,
      query,
      sort,
      format,
      fields,
      locales: locales?.map((locale) => locale.cmsId).filter(Boolean) ?? [],
      previewId,
      cmsBranchId,
    },
  ];
};

const usePrevious = <T>(value: T) => {
  const ref = React.useRef<T | undefined>(undefined);
  React.useEffect(() => {
    ref.current = value;
  });
  return ref.current;
};

export function useCollectionItemsPage(
  collection: CollectionType | null = null,
  {
    page = 0,
    limit = DEFAULT_PAGE_LIMIT,
    query = '',
    sort = DEFAULT_SORT,
    format,
    fields = '',
    enabled = true,
    locales,
  }: CMSItemsQueryOptions = {}
) {
  const dispatch = useDispatch();
  const collectionId = collection?._id ?? '';
  const siteShortName = useDesignerSelector(
    selectSiteShortNameFromSharedStores
  );
  const allLocales: Locale[] = useLocalizationSelector(selectAllLocales);

  const {cmsBranchId, keepPreviousData, track} = useKeepPreviousCMSBranchData();
  const itemsQueryParams = {
    page,
    limit,
    query,
    sort,
    fields,
    format,
    locales: locales ?? allLocales,
    cmsBranchId,
  };

  const itemsQuery = useQuery<GetCollectionItemsResponse>({
    queryKey: getCollectionItemsQueryKey(collectionId, {
      page,
      limit,
      query,
      sort,
      format,
      fields,
      locales: locales ?? allLocales,
      cmsBranchId,
    }),
    queryFn: () =>
      fetchCollectionItems(collection, siteShortName, itemsQueryParams),
    enabled: enabled && Boolean(collectionId),
    keepPreviousData,
    staleTime: STALE_TIME,
  });
  track([itemsQuery]);

  const {status, data} = itemsQuery;
  const previousItems = usePrevious(data?.items);

  React.useEffect(() => {
    if (status !== 'success' || !data?.items) {
      return;
    }

    const mergeExistingItemsTogether =
      format === 'itemRef' || format === 'withoutHeavyFields';

    /**
        React-query gives us a referentially instable value here, but they could be deep equal
        so we have to do that logic ourselves here because react-query doesn't do it for us
       */
    if (isEqual(previousItems, data.items) && !mergeExistingItemsTogether) {
      return;
    }

    // NOTE: this dispatch is to sync the items from fetching in react-query to being readable in
    // a flux context
    dispatch(receiveRawItems({items: data.items, mergeExistingItemsTogether}));
  }, [status, data, dispatch, format, previousItems]);

  return {
    ...itemsQuery,
    data: itemsQuery.data || {
      url: '',
      object: 'items',
      items: [],
      count: 0,
      limit: 0,
      offset: 0,
      collectionId: '',
      hasMore: false,
      total: 0,
    },
  };
}

const commonQueryOptionBuilder = ({
  collectionId,
  dispatch,
  fields,
  format,
  siteShortName,
  enabled,
  locales,
  cmsBranchId,
  keepPreviousData,
}: {
  collectionId: string;
  dispatch: Dispatch;
  fields: string;
  format: CMSItemsQueryOptions['format'];
  siteShortName: string | null | undefined;
  enabled: CMSItemsQueryOptions['enabled'];
  locales?: CMSItemsQueryOptions['locales'];
  cmsBranchId: string | null;
  keepPreviousData: boolean;
}) => {
  // We can't key the Map by a Collection record, so we just make a fake record
  // here, which is fine because the only thing that matters down the line is the _id
  const collection = {_id: collectionId} as CollectionType;

  return {
    queryKey: getCollectionItemsQueryKey(collectionId, {
      page: 0,
      limit: DEFAULT_PAGE_LIMIT,
      query: '',
      sort: DEFAULT_SORT,
      format,
      fields,
      locales,
      cmsBranchId,
    }),
    queryFn: () =>
      fetchCollectionItems(collection, siteShortName, {
        fields,
        format,
        locales,
        cmsBranchId,
      }),
    enabled: enabled && Boolean(collectionId),
    // The query key is derived from the exact set of referenced ids, so adding
    // or removing one produces a brand-new key with no cached data — which
    // reports `loading` and makes consumers disable the field mid-edit. Keeping
    // the previous data means an add/remove keeps rendering the known items
    // while the smaller/larger set refetches in the background. Matches
    // useCollectionItemsPage above.
    keepPreviousData,
    onSuccess: (successResponse: {
      items: Array<ItemRecordType>;
      collectionId: string;
    }) => {
      // NOTE: this dispatch is to sync the items from fetching in react-query to being readable in
      // a flux context
      dispatch(
        receiveRawItems({
          items: successResponse.items,
          mergeExistingItemsTogether:
            format === 'itemRef' || format === 'withoutHeavyFields',
        })
      );
    },
  };
};

const OBJECT_ID_RE = /^[0-9a-f]{24}$/i;

export function useReferencedCollectionItems(
  referencedItemIdsByCollectionId: ReferencedCollectionItems = {},
  {
    format = 'itemRef',
    enabled = true,
    locales,
  }: {
    format?: CMSItemsQueryOptions['format'];
    enabled?: CMSItemsQueryOptions['enabled'];
    locales?: CMSItemsQueryOptions['locales'];
  } = {}
) {
  const dispatch = useDispatch();
  const siteShortName = useDesignerSelector(
    selectSiteShortNameFromSharedStores
  );
  const {cmsBranchId, keepPreviousData, track} = useKeepPreviousCMSBranchData();

  const queryOptions: Array<
    UseQueryOptions<
      GetCollectionItemsResponse,
      unknown,
      GetCollectionItemsResponse
    >
  > = [];

  for (const [collectionId, _itemIds] of Object.entries(
    referencedItemIdsByCollectionId
  )) {
    if (!_itemIds || !Array.isArray(_itemIds) || _itemIds.length < 1) {
      continue;
    }

    const itemIds = _itemIds.filter((id) => OBJECT_ID_RE.test(String(id)));
    // Sorted so the query key (built from this order below) is stable across
    // re-renders that only reorder the same ids (e.g. dragging a CMS
    // multi-reference chip) — Immutable's Set iteration order depends on
    // insertion order, so leaving it unsorted made every reorder look like a
    // brand-new, uncached query and briefly disable the field while it "loaded".
    const uniqueItemIds = Set(itemIds).toArray().sort();

    const fieldsChunked = chunk(
      uniqueItemIds.map((id) => `fields[_id][idin][]=${id}`),
      DEFAULT_PAGE_LIMIT
    );

    fieldsChunked.forEach((fields) => {
      queryOptions.push(
        commonQueryOptionBuilder({
          collectionId,
          dispatch,
          fields: fields.join('&'),
          format,
          siteShortName,
          enabled,
          locales,
          cmsBranchId,
          keepPreviousData,
        })
      );
    });
  }

  const results = useQueries({queries: queryOptions});
  track(results);
  return results;
}

// A slug can match one row per requested locale, and the response is capped at
// DEFAULT_PAGE_LIMIT with no pagination follow-up here, so shrink the chunk by
// the locale count rather than silently dropping the overflow.
const chunkSlugFilters = (slugs: string[], locales?: Locale[]) => {
  const uniqueSlugs = Set(slugs).toArray();
  const rowsPerSlug = (locales?.length ?? 0) + 1;
  const slugsPerChunk = Math.max(
    1,
    Math.floor(DEFAULT_PAGE_LIMIT / rowsPerSlug)
  );

  return chunk(
    uniqueSlugs.map((slug) => `fields[slug][in]=${slug}`),
    slugsPerChunk
  );
};

// Every chunk is on the blocking path before a CSV import can start minting, and
// the chunk count grows with both slug count and locale count, so cap how many
// are in flight at once.
const MAX_CONCURRENT_SLUG_REQUESTS = 6;

/**
 * Imperative counterpart to useReferencedCollectionItemsBySlug, for callers that
 * must have the referenced items in hand before they act — the CSV import
 * resolves reference cells slug -> id while minting, and cannot rely on a
 * prefetch having landed in the store by then.
 *
 * Partial results are kept: one failed chunk returns its error alongside the
 * items the other chunks did resolve, rather than discarding all of them.
 */
export const fetchReferencedCollectionItemsBySlug = async (
  referencedItemSlugsByCollectionId: ReferencedCollectionItems = {},
  siteShortName: string | null | undefined,
  {
    format = 'itemRef',
    locales,
  }: {
    format?: CMSItemsQueryOptions['format'];
    locales?: CMSItemsQueryOptions['locales'];
  } = {}
): Promise<{items: Array<ItemRecordType>; errors: unknown[]}> => {
  const requests: Array<() => Promise<GetCollectionItemsResponse>> = [];

  for (const [collectionId, slugs] of Object.entries(
    referencedItemSlugsByCollectionId
  )) {
    if (!slugs || !Array.isArray(slugs) || slugs.length < 1) {
      continue;
    }

    // Only the _id is read downstream, matching commonQueryOptionBuilder.
    const collection = {_id: collectionId} as CollectionType;

    chunkSlugFilters(slugs, locales).forEach((fields) => {
      requests.push(() =>
        fetchCollectionItems(collection, siteShortName, {
          fields: fields.join('&'),
          format,
          locales,
        })
      );
    });
  }

  const items: Array<ItemRecordType> = [];
  const errors: unknown[] = [];

  for (let i = 0; i < requests.length; i += MAX_CONCURRENT_SLUG_REQUESTS) {
    const batch = requests.slice(i, i + MAX_CONCURRENT_SLUG_REQUESTS);
    const settled = await Promise.allSettled(batch.map((run) => run()));

    settled.forEach((result) => {
      if (result.status === 'fulfilled') {
        items.push(...(result.value.items ?? []));
      } else {
        errors.push(result.reason);
      }
    });
  }

  return {items, errors};
};

export function useReferencedCollectionItemsBySlug(
  referencedItemSlugsByCollectionId: ReferencedCollectionItems = {},
  {
    format = 'itemRef',
    locales,
  }: {
    format?: CMSItemsQueryOptions['format'];
    locales?: CMSItemsQueryOptions['locales'];
  } = {}
) {
  const dispatch = useDispatch();
  const siteShortName = useDesignerSelector(
    selectSiteShortNameFromSharedStores
  );
  const {cmsBranchId, keepPreviousData, track} = useKeepPreviousCMSBranchData();

  const queryOptions: Array<
    UseQueryOptions<
      GetCollectionItemsResponse,
      unknown,
      GetCollectionItemsResponse
    >
  > = [];

  for (const [collectionId, slugs] of Object.entries(
    referencedItemSlugsByCollectionId
  )) {
    if (!slugs || !Array.isArray(slugs) || slugs.length < 1) {
      continue;
    }
    const fieldsChunked = chunkSlugFilters(slugs, locales);

    fieldsChunked.forEach((fields) => {
      queryOptions.push(
        commonQueryOptionBuilder({
          collectionId,
          dispatch,
          fields: fields.join('&'),
          format,
          siteShortName,
          enabled: true,
          locales,
          cmsBranchId,
          keepPreviousData,
        })
      );
    });
  }

  const results = useQueries({queries: queryOptions});
  track(results);
  return results;
}
